import { useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Grid2 as Grid, Skeleton } from '@mui/material'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { DaiDivider } from '@/components/DaiDivider'
import UpgradeWithScreenshot from '@/components/UpgradeWithScreenshot'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_ARRAY_SETTINGS,
  getTenantArraySettingsQuery,
  getTenantArraySettingsQueryVariables,
} from '@/graphql'
import useActiveSubscription from '@/hooks/useActiveSubscription'
import { usePermissions } from '@/hooks/usePermissions'
import { useSetDefaultTimeRange } from '@/hooks/useSetDefaultTimeRange'
import useT from '@/i18n/useT'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DateHelpers } from '@/utils/date.helpers'

import { HomeDashboardParams } from '../dashboards/Default/HomeSummary'
import ProductCategoriesTable from '../dashboards/Default/ProductCategoriesTable'
import { ProductCategoryRevPieChart } from '../dashboards/Default/ProductCategoryRevPieChart'
import { FuelVolumeChart } from './components/FuelVolumeChart'
import { InsideVsOutsideSalesChart } from './components/InsideVsOutsideSalesChart'
import { RevAndFuelStatCards } from './components/RevAndFuelStatCards'
import SalesCategorySettings, {
  SalesCategorySetting,
} from './components/SalesCategorySettings'
import { SalesCategorySettingsPanel } from './components/SalesCategorySettingsPanel'
import StoreRevFuelTable from './components/StoreRevFuelTable'

const RevenueDetails = () => {
  const { t } = useT()
  const { permissions } = usePermissions()
  const { isPremium } = useActiveSubscription()
  const {
    state: urlState,
    handle: { handleUpdateSearchParams, handleNavTo },
  } = useUrlSearchParams<HomeDashboardParams>()
  const { id: singleStoreId } = useParams()
  const { startDate, endDate, storeIds: storeIdsFromQueryParams } = urlState
  const [settingToUpdate, setSettingToUpdate] = useState<
    SalesCategorySetting | null | undefined
  >(null)
  const storeIds = singleStoreId ? [singleStoreId] : storeIdsFromQueryParams
  const startDateWithTime = startDate
    ? DateHelpers.getStartOfDayWithTimezone(startDate)
    : null
  const endDateWithTime = endDate
    ? DateHelpers.getEndOfDayWithTimezone(endDate)
    : null

  useSetDefaultTimeRange({
    startDate,
    endDate,
    updateSearchParams: handleUpdateSearchParams,
    timeRange: 'yesterday',
  })

  const {
    data,
    loading: loadingCategories,
    refetch: refetchCategories,
  } = useQuery<
    getTenantArraySettingsQuery,
    getTenantArraySettingsQueryVariables
  >(GET_TENANT_ARRAY_SETTINGS, {
    skip: !isPremium,
  })

  const insideSalesCategories =
    data?.tenantArraySettings?.insideSalesCategories || []
  const outsideSalesCategories =
    data?.tenantArraySettings?.outsideSalesCategories || []
  const fuelSalesCategories =
    data?.tenantArraySettings?.fuelSalesCategories || []

  useRefresh(refetchCategories)

  const handleUpgradeClick = () => {
    handleNavTo('/pricing')
  }

  return (
    <>
      {isPremium ? (
        <>
          {!!startDateWithTime && !!endDateWithTime && !loadingCategories ? (
            <>
              <SalesCategorySettingsPanel storeIds={storeIds} />
              <RevAndFuelStatCards
                onSettingsClick={setSettingToUpdate}
                startDate={startDateWithTime}
                endDate={endDateWithTime}
                storeIds={storeIds}
                insideCategories={insideSalesCategories}
                outsideCategories={outsideSalesCategories}
                fuelCategories={fuelSalesCategories}
              />
              <DaiDivider my={3} />
              {!singleStoreId && (
                <>
                  <StoreRevFuelTable
                    filters={{
                      startDate: startDateWithTime,
                      endDate: endDateWithTime,
                      storeIds,
                      insideCategories: insideSalesCategories,
                      outsideCategories: outsideSalesCategories,
                      fuelCategories: fuelSalesCategories,
                      groupByStore: true,
                    }}
                    showHeader={false}
                  />
                  <DaiDivider my={3} />
                </>
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, xl: 6 }}>
                  <InsideVsOutsideSalesChart
                    storeIds={storeIds}
                    {...ChartHelpers.getChartWindow(
                      startDateWithTime,
                      endDateWithTime,
                      new Date().toISOString(),
                    )}
                    outsideCategories={outsideSalesCategories}
                    insideCategories={insideSalesCategories}
                    annotationStartDate={startDateWithTime}
                    annotationEndDate={endDateWithTime}
                    needToSetUpCategories={
                      !insideSalesCategories.length ||
                      !outsideSalesCategories.length
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, xl: 6 }}>
                  <FuelVolumeChart
                    storeIds={storeIds}
                    {...ChartHelpers.getChartWindow(
                      startDateWithTime,
                      endDateWithTime,
                      new Date().toISOString(),
                    )}
                    categories={
                      fuelSalesCategories.length
                        ? fuelSalesCategories
                        : ['MANUAL FUEL DEPT'] // TODO: this blows up with no categories are sent???
                    }
                    annotationStartDate={startDateWithTime}
                    annotationEndDate={endDateWithTime}
                    needToSetFuelSalesCategories={!fuelSalesCategories.length}
                  />
                </Grid>
              </Grid>
              <DaiDivider my={3} />
              <ProductCategoriesTable
                filters={{
                  startDate: startDateWithTime,
                  endDate: endDateWithTime,
                  storeIds,
                }}
                singleStoreId={singleStoreId}
              />
              <DaiDivider my={3} />
              <ProductCategoryRevPieChart
                startDate={startDateWithTime}
                endDate={endDateWithTime}
                storeIds={storeIds}
              />
            </>
          ) : (
            <>
              <Skeleton height={500} />
              <Skeleton height={500} />
              <Skeleton height={500} />
            </>
          )}
          <SalesCategorySettings
            setting={settingToUpdate}
            open={!!settingToUpdate}
            onClose={() => setSettingToUpdate(null)}
          />
        </>
      ) : (
        <UpgradeWithScreenshot
          titleLabel={t('revenue.basic.premiumDescriptionHeader')}
          descriptionLabel={t('revenue.basic.premiumDescription')}
          buttonLabel={t('revenue.button.upgrade')}
          viewPricingLabel={t('upgradeMessage.button.viewPricing')}
          screenshotName="Revenue"
          onUpgradeClick={handleUpgradeClick}
          canManageBilling={permissions.CAN_MANAGE_BILLING}
        />
      )}
    </>
  )
}

export default RevenueDetails
